/**
 * Quiz Buzz host entry.
 *
 * Boots the joystick host SDK in multi-player mode, generates one
 * pairing QR (4-8 players share it; the broker assigns each phone a
 * fresh slot), runs the world step at 30Hz, and re-renders DOM on
 * every step. Re-render granularity is coarse but the screen is
 * mostly text — perfectly fine without canvas.
 */

import './style.css';
import '@forgesworn/joystick-pairing-ui/style.css';
import { mountGameControls } from '@forgesworn/joystick-pairing-ui';
import QRCode from 'qrcode';
import listingSource from './listing.json';
import { checkGameListing, controllerSpecFromListing } from '@forgesworn/joystick-protocol';

// Smart-TV browsers (Tizen, WebOS) ship Blink versions from 2018-2020
// that pre-date Element.replaceChildren (Chromium 86, Oct 2020). The
// renderer leans on it heavily — without the polyfill the very first
// frame throws TypeError and the screen stays black.
if (typeof Element !== 'undefined' && !Element.prototype.replaceChildren) {
  (Element.prototype as { replaceChildren?: (...nodes: Array<Node | string>) => void }).replaceChildren =
    function (this: Element, ...nodes: Array<Node | string>): void {
      while (this.firstChild) this.removeChild(this.firstChild);
      this.append(...nodes);
    };
}

import { startControllerHost, type ControllerHost, type ControllerSpec } from '@forgesworn/joystick-host';
import {
  addPlayer, applyInput, isAnswerPress, MAX_PLAYERS, newWorld,
  QUESTION_TIME_S, removePlayer, step, tryStart, type World,
} from './game.js';
import { acceptName, answerFeedback, pauseMessage, playerSpec, scoreLabel } from './controller-strings.js';
import { render, type RenderFrame } from './render.js';
import {
  setAudioPaused, initAudio, isMuted, sfxClick, sfxCorrect, sfxFinal, sfxReveal,
  sfxRoundStart, sfxTick, sfxWrong, toggleMuted,
} from './audio.js';

// Controls come from the game's published listing (src/listing.json), so the
// catalogue entry and the controller the phone draws cannot drift apart.
const listingCheck = checkGameListing(listingSource);
if (!listingCheck.ok) throw new Error(`Invalid game listing: ${listingCheck.errors.join('; ')}`);
const LISTING_SPEC = controllerSpecFromListing(listingCheck.listing)!;

const PAD_URL = import.meta.env.VITE_PAD_URL ?? 'https://pad.forgesworn.dev';
const BROKER = import.meta.env.VITE_CONTROLLER_WS ?? 'wss://controller.pallasite.app/';
const PLAYER_COLOURS = ['#58ff58', '#ff8a3a', '#b48cff', '#ffd84a', '#ff5fa5', '#5fdbff', '#ffa55f', '#a5ff5f'];

const app = document.getElementById('app');
if (!app) throw new Error('app root missing');

const world: World = newWorld();
let qrDataUrl: string | null = null;

const host: ControllerHost = startControllerHost({
  ws: BROKER,
  multi: true,
  pwaUrl: PAD_URL,
});

let bootHidden = false;
host.onStatus((s) => {
  const bootEl = document.getElementById('boot');
  const reconnectEl = document.getElementById('reconnect');
  if (s.kind === 'waiting' || s.kind === 'paired') {
    if (!bootHidden && bootEl) {
      bootEl.classList.add('hidden');
      setTimeout(() => bootEl.remove(), 600);
      bootHidden = true;
    }
    if (reconnectEl) {
      reconnectEl.classList.remove('show');
      reconnectEl.classList.add('hidden');
    }
  } else if (s.kind === 'reconnecting' || s.kind === 'closed') {
    if (bootHidden && reconnectEl) {
      reconnectEl.classList.add('show');
      reconnectEl.classList.remove('hidden');
    }
  }
});

void QRCode.toDataURL(host.pairingUrl, {
  margin: 2,
  width: 1200,
  errorCorrectionLevel: 'M',
  color: { dark: '#0a0a12', light: '#ffffff' },
}).then((url) => { qrDataUrl = url; }).catch((err) => {
  console.warn('[quiz] QR generation failed, lobby falls back to URL text', err);
});

const readyPlayer: Parameters<ControllerHost['onPlayerJoin']>[0] = (player) => {
  const colour = PLAYER_COLOURS[player.p % PLAYER_COLOURS.length]!;
  // If a bot was sitting in this slot, kick it so the human takes
  // their place with a fresh score. addPlayer skips the create if the
  // slot is already occupied, so we explicitly remove first.
  if (!world.players.has(player.p) || world.players.get(player.p)!.ai) {
    removePlayer(world, player.p);
    addPlayer(world, player.p, colour, /* ai */ false);
  }
  // The listing's four option buttons are coloured to match the option
  // tiles on the big screen, so a player can glance at their pad.
  const spec: ControllerSpec = playerSpec(LISTING_SPEC, player.p);
  host.sendToPlayer(player.p, { type: 'controller-spec', spec });
  sendAnswerFeedback(player.p);
};
host.onPlayerJoin(readyPlayer);
host.onPlayerResume(readyPlayer);

// Tournament context — phones announce their running total + chosen
// name on pair-up. Quiz threads the name through to the scoreboard.
const tournamentTotals = new Map<number, number>();
const playerNames = new Map<number, string>();
host.onPhoneContext((p, ctx) => {
  if (ctx) {
    tournamentTotals.set(p, ctx.tournamentTotal);
    const name = acceptName(ctx.name);
    if (name) playerNames.set(p, name);
    else playerNames.delete(p);
  } else {
    tournamentTotals.delete(p);
    playerNames.delete(p);
  }
});

host.onPlayerLeave((player) => {
  removePlayer(world, player.p);
  tournamentTotals.delete(player.p);
  // Slot opens — autoFillAI on the next tick will drop a bot back in.
});

host.onInput((frame) => {
  // Default to slot 0 if the broker dropped p (older broker, single
  // mode). Keeps single-phone play working without multi routing.
  const p = typeof frame.p === 'number' ? frame.p : 0;
  // Locked native pads release their controls in response to feedback. Those
  // releases must not produce more locked feedback and another release batch.
  if (gameControls.paused || !isAnswerPress(frame.k, frame.v)) return;
  initAudio();
  applyInput(world, p, frame.k, frame.v);
  sendAnswerFeedback(p);
});

const gameControls = mountGameControls({
  canStart: () => world.phase.kind === 'lobby' && host.players.some(player => player.connected),
  start: () => { initAudio(); tryStart(world); },
  restart: () => { Object.assign(world, newWorld()); for (const player of host.players) readyPlayer(player); tryStart(world); },
  pause: (paused) => { setAudioPaused(paused); if (paused) host.broadcast(pauseMessage()); else for (const player of host.players) sendAnswerFeedback(player.p);  },
  muted: isMuted,
  toggleSound: () => { initAudio(); toggleMuted(); },
});

window.addEventListener('keydown', (e) => {
  if (e.key === 'm' || e.key === 'M') { initAudio(); toggleMuted(); }
});
window.addEventListener('click', () => initAudio());

// Quiz is text-heavy; 30Hz keeps the timer bar smooth without
// hammering DOM rebuilds. Render is idempotent — replaceChildren is
// cheap given the screen is sub-100 elements.
// World stays empty until a human pairs + presses any A/B/X/Y. No
// auto-start, no cold-boot bots — the screen shows scan-to-play
// until someone actually wants to play.
//
// Audio diff: phase transitions trigger the round-start / reveal /
// final fanfares; per-player pick going null → set fires a click;
// at the reveal we play correct or wrong once (not per player); the
// last 5 seconds of a question tick.
function sendAnswerFeedback(p: number): void {
  const player = world.players.get(p); if (!player || player.ai) return;
  host.sendToPlayer(p, answerFeedback({ phase: world.phase.kind, pick: player.pick, lastCorrect: player.lastCorrect, lastDelta: player.lastDelta, score: player.score }));
}

let last = performance.now();
let prevPhaseKind: World['phase']['kind'] = 'lobby';
let prevQuestionIndex = -1;
const prevPicks = new Map<number, 0 | 1 | 2 | 3 | null>();
let lastTickedSec = -1;
function loop(t: number): void {
  const dt = Math.min(0.1, (t - last) / 1000);
  last = t;
  if (!gameControls.paused) step(world, dt);
  gameControls.update();

  const muted = isMuted();
  const phase = world.phase;
  const kind = phase.kind;
  if (kind !== prevPhaseKind) for (const player of host.players) sendAnswerFeedback(player.p);
  if (kind === 'pre-round' && prevPhaseKind !== 'pre-round') {
    // Brand-new round: index changes on every transition through
    // pre-round, so we use that to gate the start fanfare exactly
    // once per round.
    const ix = phase.round.index;
    if (ix !== prevQuestionIndex) {
      if (!muted) sfxRoundStart();
      prevQuestionIndex = ix;
    }
  } else if (kind === 'reveal' && prevPhaseKind !== 'reveal') {
    if (!muted) sfxReveal();
    // Per-human haptic on reveal: pulse if their answer was right,
    // fail if wrong. Bots get nothing — no phone to vibrate.
    for (const player of world.players.values()) {
      if (player.ai) continue;
      if (player.lastCorrect) host.haptic('pulse', player.p);
      else if (player.pick !== null) host.haptic('fail', player.p);
    }
    // After the whoosh, play correct / wrong once based on whether
    // ANY paying-attention player got it right (mix of bots + humans
    // is fine — the reveal SFX is for the room, not per-player).
    setTimeout(() => {
      if (isMuted()) return;
      const someoneRight = [...world.players.values()].some((p) => p.lastCorrect);
      if (someoneRight) sfxCorrect(); else sfxWrong();
    }, 220);
  } else if (kind === 'final' && prevPhaseKind !== 'final') {
    if (!muted) sfxFinal();
    // Final standings: award each human their quiz score as tournament
    // points (1pt per correct answer), and crown the winner(s) with a
    // win haptic.
    const players = [...world.players.values()];
    const top = players.reduce((acc, p) => Math.max(acc, p.score), 0);
    for (const player of players) {
      if (player.ai) continue;
      if (player.score === top && top > 0) host.haptic('win', player.p);
      if (player.score > 0) {
        const label = scoreLabel(player.score, top);
        host.awardScore(player.p, player.score, label);
        tournamentTotals.set(player.p, (tournamentTotals.get(player.p) ?? 0) + player.score);
      }
    }
  }
  // Per-player pick click — only when a player just locked their
  // answer (was null, now set, in 'question' phase). Skip bots —
  // they're host-side, no one pressed a real button.
  if (kind === 'question') {
    for (const player of world.players.values()) {
      const prev = prevPicks.get(player.p) ?? null;
      if (prev === null && player.pick !== null && !player.ai) {
        if (!muted) sfxClick();
        host.haptic('tap', player.p);
      }
    }
    // Countdown tick on whole seconds for the last 5.
    const elapsed = world.time - phase.startedAt;
    const remaining = QUESTION_TIME_S - elapsed;
    const remainingSec = Math.ceil(remaining);
    if (remaining < 5 && remaining > 0 && remainingSec !== lastTickedSec) {
      if (!muted) sfxTick();
      lastTickedSec = remainingSec;
    }
  } else {
    lastTickedSec = -1;
  }
  // Refresh pick cache regardless of phase.
  for (const player of world.players.values()) prevPicks.set(player.p, player.pick);
  prevPhaseKind = kind;

  const frame: RenderFrame = { world, pairingUrl: host.pairingUrl, qrDataUrl, sessionId: host.sessionId, muted, tournamentTotals, playerNames };
  render(app!, frame);
  setTimeout(() => requestAnimationFrame(loop), 1000 / 30 - 1);
}
requestAnimationFrame(loop);

console.log(`[quiz] supports ${MAX_PLAYERS} players`);
